import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap';
import { EmailRatesComponent } from 'src/app/component/email-rates/email-rates.component';
import { DlpService } from 'src/app/service/dlp.service';
import { PricingService } from 'src/app/service/pricing.service';
import { WebConfigurationService } from 'src/app/web-configuration-module/web-configuration.service';

@Component({
  selector: 'app-personalized-quote',
  templateUrl: './personalized-quote.component.html',
  styleUrls: ['./personalized-quote.component.scss']
})
export class PersonalizedQuoteComponent implements OnInit {
  @ViewChild('personalizedQuote', { static: true }) personalizedQuote!: ElementRef;
  @Input() refId: string;
  @Input() lpSupportEmail: string;

  tabs: string[] = [];
  selectedTab: number = 0;
  result: any[] = []; // Example result data
  resultsLoading: boolean = false;
  emailSent: boolean = false;
  loading = false;
  showSuccess = false;

  bsModalRef: BsModalRef;
  personaliseQuoteForm: any;
  currentStep = 1;
  min = 0;
  max = 2000000 * 0.75;
  search: any;
  mortgagePricerRef: any;
  contact = {
    propertyAddress: {
      state: '',
      street: '',
      streetCont: '',
      city: '',
      zip: '',

    },
    firstname: '',
    lastname: '',
    email: '',
    phone: ''
  };
  
  creditScores = [
    { label: 'Fair', value: '560 - 669',class: 'fair', id: 560 },
    { label: 'Good', value: '670 - 739', class: 'good', id: 670 },
    { label: 'Very Good', value: '740 - 799', class: 'verygood', id: 740 },
    { label: 'Excellent', value: '800+', class: 'excellent', id: 800 }
  ];
  purchaseSteps = [
    {
      label: 'Loan Type',
      key: 'loanType'
    },
    {
      label: 'Property Type',
      key: 'propertyType'
    },
    {
      label: 'Decision',
      key: 'decisionProcess'
    },
    {
      label: 'Purpose',
      key: 'loanPurpose'
    },
    {
      label: 'Location',
      key: 'location'
    },
    {
      label: 'Value',
      key: 'propertyValue'
    },
    {
      label: 'Downpayment',
      key: 'downPayment'
    },
    {
      label: 'Income',
      key: 'annualHouseholdIncome'
    },
    {
      label: 'Credit Score',
      key: 'creditScore'
    },
    {
      label: 'Veteran',
      key: 'spouseServedInMilitary'
    },
    {
      label: 'Email',
      key: 'email'
    },
    {
      label: 'Rates',
      key: 'rates'
    }
  ]

  refinanceSteps = [
    {
      label: 'Loan Type',
      key: 'loanType'
    },
    {
      label: 'Property Type',
      key: 'propertyType'
    },
    {
      label: 'Decision',
      key: 'decisionProcess'
    },
    {
      label: 'Purpose',
      key: 'loanPurpose'
    },
    {
      label: 'Location',
      key: 'location'
    },
    {
      label: 'Value',
      key: 'propertyValue'
    },
    {
      label: 'Current Loan',
      key: 'currentLoan'
    },
    {
      label: 'Cashout',
      key: 'cashoutAmount'
    },
    {
      label: 'Income',
      key: 'annualHouseholdIncome'
    },
    {
      label: 'Credit Score',
      key: 'creditScore'
    },
    {
      label: 'Veteran',
      key: 'spouseServedInMilitary'
    },
    {
      label: 'Email',
      key: 'email'
    },
    {
      label: 'Rates',
      key: 'rates'
    }
  ]
  selectedLoanCriteria: any;
  selectedLoan: string = 'purchase'; // Default selection
  scenarios = [];

  selectLoan(type: string) {
    this.selectedLoan = type;
  }

  constructor(
    private modalService: BsModalService,
    private fb: FormBuilder,
    private pricingService: PricingService,
    private dlpService: DlpService,
    private http: HttpClient,
    private configurationService: WebConfigurationService,
    private activatedRoute: ActivatedRoute,
  ) { 
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['refId']) {
        this.refId = params['refId'] || '67b9b98bce8ad00001e43b9a';
      }
      const primary = params.primaryColor ? `#${params.primaryColor}` :'#E63A27';
      const secondary = params.secondaryColor ? `#${params.secondaryColor}` : '#838383';
      this.updateTheme(primary, secondary);
    });
  }

  ngOnInit() {
    this.personaliseQuoteForm = this.fb.group({
      loanType: ['', Validators.required],
      propertyType: ['', Validators.required],
      decisionProcess: ['', Validators.required],
      residencyType: ['', Validators.required],
      propertyValue: ['', Validators.required],
      downPayment: ['', Validators.required],
      currentLoan: ['', Validators.required],
      cashOut: [0, Validators.required],
      annualHouseholdIncome: ['', Validators.required],
      creditScore: [this.creditScores[1].id, Validators.required],
      spouseServedInMilitary: [false, Validators.required],
      email: ['', Validators.required],
      phone: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      county: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', Validators.required]
    });
  }

  get isLoanTypeRefinance() {
    return this.personaliseQuoteForm && this.personaliseQuoteForm.get('loanType').value === 'Refinance';
  }

  get totalSteps() {
    return this.isLoanTypeRefinance ? this.refinanceSteps.length + 1 : this.purchaseSteps.length + 1;
  }

  get totalStepsArray() {
    return Array(this.totalSteps).fill(0).map((x, i) => i + 1);
  }

  get downPaymentValue() {
    return this.personaliseQuoteForm.get('downPayment').value;
  }

  get currentLoanValue() {
    return this.personaliseQuoteForm.get('currentLoan').value;
  }

  get homePriceValue() {
    return this.personaliseQuoteForm.get('propertyValue').value;
  }

  get loanAmount() {
    return this.homePriceValue - this.downPaymentValue;
  }

  get isEmailStep() {
    return this.isLoanTypeRefinance ? this.refinanceSteps[this.currentStep - 1].key === 'email' : this.purchaseSteps[this.currentStep - 1].key === 'email';
  }

  get creditScoreValue() {
    return this.personaliseQuoteForm.get('creditScore').value;
  }

  get isVeteran() {
    return this.personaliseQuoteForm.get('spouseServedInMilitary').value;
  }

  getCurrentStepLabel(): string {
    return this.isLoanTypeRefinance ? this.refinanceSteps[this.currentStep - 1].label : this.purchaseSteps[this.currentStep - 1].label;
  }

  updateHomePrice(event: string) {
    const numericValue = event.replace(/\D/g, '');
    this.personaliseQuoteForm.get('propertyValue').setValue(+numericValue);
    this.personaliseQuoteForm.get('downPayment').setValue(this.homePriceValue * 0.20);
    this.personaliseQuoteForm.get('currentLoan').setValue(this.homePriceValue * 0.80);
  }

  updateTheme(primary: string, secondary: string): void {
    document.documentElement.style.setProperty('--primary-color', primary);
    document.documentElement.style.setProperty('--secondary-color', secondary);
  }

  updateAnnualIncome(event: string) {
    const numericValue = event.replace(/\D/g, '');
    this.personaliseQuoteForm.get('annualHouseholdIncome').setValue(+numericValue);
  }

  updateCashOut(event: string) {
    const numericValue = event.replace(/\D/g, '');
    this.personaliseQuoteForm.get('cashOut').setValue(+numericValue);
  }

  nextStep() {
    const key = this.isLoanTypeRefinance ? this.refinanceSteps[this.currentStep - 1].key : this.purchaseSteps[this.currentStep - 1].key;
    if (this.currentStep < this.totalSteps && this.validateCurrentStep(key)) {
      this.currentStep++;
    } else {
      console.error('Form is invalid');
    }

    if (this.isLoanTypeRefinance && this.refinanceSteps[this.currentStep - 1].key === 'rates' ||
      !this.isLoanTypeRefinance && this.purchaseSteps[this.currentStep - 1].key === 'rates'
     ) {
      this.searchRates();
    }
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  getDetailInfoFromZipCode(zipCode: string, callback: (data) => void, errCallback: (err) => void) {
    this.http.get(
      this.configurationService.configuration.ppeApi
      + 'constant/zip/listMiniPricer/' + zipCode).pipe(
    )
      .subscribe(
        data => callback(data),
        err => errCallback(err),
      );
  }

  searchForZip(zip) {
    this.personaliseQuoteForm.get('zip').setValue(zip);
    if (zip && zip.length === 5) {
      this.getDetailInfoFromZipCode(zip, data => {
        if (data && data.length > 0) {
          this.personaliseQuoteForm.get('county').setValue(data[0].county);
          this.personaliseQuoteForm.get('city').setValue(data[0].primary_city);
          this.personaliseQuoteForm.get('state').setValue(data[0].state);
          this.contact.propertyAddress.state = data[0].state;
          this.contact.propertyAddress.city = data[0].primary_city;
          // this.contact.propertyAddress.c = zip;
          this.contact.propertyAddress.zip = zip;
        }
      },
        err => {
          console.error(err);
        });
    } else {
      this.personaliseQuoteForm.get('county').setValue('');
      this.personaliseQuoteForm.get('city').setValue('');
      this.personaliseQuoteForm.get('state').setValue('');
      this.contact.propertyAddress.state = '';
      this.contact.propertyAddress.city = '';
      this.contact.propertyAddress.zip = '';
    }
  }

  openPersonalizedQuoteModal() {
    const initialState: any = {
    };
    this.bsModalRef = this.modalService.show(this.personalizedQuote,
      {
        class: 'modal-lg modal-dialog-centered',
        animated: true,
        backdrop: true,
        ignoreBackdropClick: true,
        keyboard: false,
        initialState
      }
    );
  }

  close() {
    this.bsModalRef.hide();
  }

  saveAndContinue(key: string, value: any) {
    this.personaliseQuoteForm.get(key).setValue(value);
    this.nextStep();
  }

  getFormValue(key: string) {
    return this.personaliseQuoteForm.get(key).value;
  }

  getSliderBackground = (controlName: string): string => {
    const percentage = ((this.downPaymentValue - this.min) / (this.max - this.min)) * 100 || 0;
    return `linear-gradient(to right, #ff4444 ${percentage}%, #e0e0e0 ${percentage}%)`;
  }

  updateKey(e: string, key: string) {
    this.personaliseQuoteForm.get(key).setValue(+e);
  }

  closeModal() {
    this.bsModalRef.hide();
  }

  emailNow() {
    this.selectedLoanCriteria = this.search.loanCriterias[this.selectedTab];
    const payload = {
      ref: this.refId,
      contact: this.contact,
      search: this.search,
      loanCriteria: this.selectedLoanCriteria,
      results: this.result[this.selectedTab]
    };

    this.pricingService.sendEmail(payload).toPromise()
    .then(data => {
      this.showSuccess = true;
      setTimeout(() => {
        this.showSuccess = false;
      }, 5000);
      this.loading = false;
    })
    .catch(err => {
      console.error(err);
      this.loading = false;
    });

  }

  searchRates() {
    this.loading = true;
    this.search = null;
    this.pricingService.getSearch(this.refId).toPromise()
      .then(ref => {
        this.mortgagePricerRef = ref;
        this.search = ref.miniPricerPref.search;
        this.scenarios = ref.miniPricerPref.search.loanCriterias;
        this.search.preferredAmortization = 'Fixed';
        this.search.secondLoanAmount = 0;
        this.search.cashoutAmount = this.personaliseQuoteForm.get('cashOut').value || 0;
        this.search.loanPurpose = this.personaliseQuoteForm.get('loanType').value;
        this.search.propertyUse = this.personaliseQuoteForm.get('residencyType').value;
        this.search.propertyType = this.personaliseQuoteForm.get('propertyType').value;
        this.search.homeValue = this.personaliseQuoteForm.get('propertyValue').value;
        this.search.amount = this.loanAmount;
        this.search.downPayment = this.personaliseQuoteForm.get('downPayment').value;
        this.search.fico = this.creditScoreValue;
        this.search.zip = this.personaliseQuoteForm.get('zip').value;
        this.search.state = this.personaliseQuoteForm.get('state').value;
        this.search.county = this.personaliseQuoteForm.get('county').value;
        this.search.firstTimeHomeBuyer = this.personaliseQuoteForm.get('residencyType').value === 'Primary Residence';
        this.triggerSearch();
        this.loading = false;
      })
      .catch(err => {
        this.loading = false;
        console.error(err);
      });
  }

  setActiveTab(index: number): void {
    this.selectedTab = index;
  }

  triggerSearch() {
    const searchLoanPurpose = this.mortgagePricerRef.miniPricerPref.search.loanPurpose
    if ('miniPricerPref' in this.mortgagePricerRef) {
      if ('originalSearch' in this.mortgagePricerRef.miniPricerPref) {
        if ('loanPurposeCriteria' in this.mortgagePricerRef.miniPricerPref.originalSearch) {
          if (this.mortgagePricerRef.miniPricerPref.originalSearch.loanPurposeCriteria.length > 0) {
            if (this.mortgagePricerRef.miniPricerPref.originalSearch.loanPurposeCriteria !== searchLoanPurpose) {
              this.mortgagePricerRef.miniPricerPref.originalSearch.loanPurposeCriteria = [searchLoanPurpose]
            }
            this.mortgagePricerRef.miniPricerPref.search.loanPurposeCriteria = [searchLoanPurpose]
          } else {
            this.mortgagePricerRef.miniPricerPref.search.loanPurposeCriteria = [searchLoanPurpose]
          }
        } else {
          this.mortgagePricerRef.miniPricerPref.search.loanPurposeCriteria = [searchLoanPurpose]
        }
        this.mortgagePricerRef.miniPricerPref.originalSearch.property.address.zip = this.search.zip;
        this.mortgagePricerRef.miniPricerPref.originalSearch.criteria.purchasePrice = this.search.homeValue;
        this.mortgagePricerRef.miniPricerPref.originalSearch.criteria.downPaymentAmount = this.search.downPayment;
        this.mortgagePricerRef.miniPricerPref.originalSearch.criteria.fico = this.search.fico;
        this.mortgagePricerRef.miniPricerPref.originalSearch.criteria.propertyUse = this.search.propertyUse;
        this.mortgagePricerRef.miniPricerPref.originalSearch.criteria.loanType = this.search.preferredAmortization
        this.mortgagePricerRef.miniPricerPref.originalSearch.loanPurposeCriteria = [this.search.loanPurpose];
        this.mortgagePricerRef.miniPricerPref.originalSearch.criteria.firstTimeHomeBuyer = this.search.firstTimeHomeBuyer;
        this.mortgagePricerRef.miniPricerPref.originalSearch.criteria.cashoutAmount = this.search.cashoutAmount;
        this.mortgagePricerRef.miniPricerPref.originalSearch.criteria.loanAmount = this.search.amount;
      }
    }

    this.resultsLoading = true;
    this.result = [];
    this.tabs = [];
    this.pricingService.search(this.mortgagePricerRef).toPromise()
      .then(data => {
        
        if(!this.isVeteran) {
          this.result = data.filter((res, i) => i !== 3 || res !== null);
          this.tabs = this.scenarios.filter((res, i) => i !== 3 && this.result.length - 1 > i).map((res) => res.name);
        } else {
          this.result = data.filter((res, i) => res !== null);;
          this.tabs = this.scenarios.filter((res, i) => this.result.length > i).map((res) => res.name);
        }
        this.resultsLoading = false;
        this.loading = false;
      })
      .catch(err => {
        console.error(err);
        this.resultsLoading = false;
        this.loading = false;
      });
  }

  validateCurrentStep(key: string): boolean {
    switch (key) {
      case 'propertyValue':
        return this.personaliseQuoteForm.get('propertyValue').valid;
      case 'downPayment':
        return this.personaliseQuoteForm.get('downPayment').valid;
      case 'currentLoan':
        return this.personaliseQuoteForm.get('currentLoan').valid;
      case 'cashoutAmount':
        return this.personaliseQuoteForm.get('cashOut').valid;
      case 'propertyType':
        return this.personaliseQuoteForm.get('propertyType').valid;
      case 'decisionProcess':
        return this.personaliseQuoteForm.get('decisionProcess').valid;
      case 'residencyType':
        return this.personaliseQuoteForm.get('residencyType').valid;
      case 'annualHouseholdIncome':
        return this.personaliseQuoteForm.get('annualHouseholdIncome').valid;
      case 'creditScore':
        return this.personaliseQuoteForm.get('creditScore').valid;
      case 'spouseServedInMilitary':
        return this.personaliseQuoteForm.get('spouseServedInMilitary').valid;
      case 'email':
        return this.contact.email !=='' && this.contact.firstname  !=='' && this.contact.lastname   !==''&& this.contact.phone  !=='';
      case 'firstName':
        return this.personaliseQuoteForm.get('firstName').valid && this.personaliseQuoteForm.get('lastName').valid;
      case 'location':
        return this.personaliseQuoteForm.get('zip').valid && this.personaliseQuoteForm.get('county').valid && this.personaliseQuoteForm.get('city').valid && this.personaliseQuoteForm.get('state').valid;
      // Add cases for other steps as needed
      default:
        return true;
    }
  }

  sendEmailToLpContact() {
    this.nextStep();
    return;
    const emailDetails = {
      templateId: '67c0a06ba21322126d56b7ca',
      contacts: [{
        name: 'LP Support',
        email: this.lpSupportEmail
      }],
      map: {
        'propertyAddress.state': this.contact.propertyAddress.state,
        'propertyAddress.street': this.contact.propertyAddress.street,
        'propertyAddress.streetCont': this.contact.propertyAddress.streetCont,
        'propertyAddress.city': this.contact.propertyAddress.city,
        'propertyAddress.zip': this.contact.propertyAddress.zip,
        'firstname': this.contact.firstname,
        'lastname': this.contact.lastname,
        'email': this.contact.email,
        'phone': this.contact.phone,
        // 'homePrice': this.homePrice,
        // 'loanAmount': this.homePrice - this.downPayment,
        // 'downPayment': this.downPayment,
        // 'downPaymentPercentage': this.downPaymen tPercentage,
        // 'interestRate': this.interestRate,
        // 'loanTerm': this.loanTerm,
        // 'loanScenario': this.loanType,
        // 'monthlyPayment': this.monthlyPayment,
        // 'homeInsuranceAmount': this.homeInsuranceAmount,
        // 'propertyTaxAmount': this.propertyTaxAmount,
        // 'apr': this.apr,
        // 'propertyTaxRate': this.propertyTaxAmount / (this.homePriceValue / 12) * 100,
        // 'hoaDuesValue': this.hoaDuesValue,
        // 'monthlyPaymentTotal': this.monthlyPaymentTotal,
        // 'ficoScore': this.ficoScore
      },
      attachments: [],
      contactsBcc: [{
        name: 'LP Support',
        email: this.lpSupportEmail
      }],
    }
    this.dlpService.sendMortgageEmail('5d64369769515b0001964707', [emailDetails]).toPromise()
      .then(data => {
        console.log(data);
      }).catch(err => {
        console.error(err);
      })
  }

}



